import { z } from "zod";
import { ArticleClaim, ArticleFlag, ArticleIntent, ArticleLink, DraftQuestion, SourceRef, TopicProposal } from "./article.js";

/**
 * The SEO tab's own shapes ([21](../../../docs/21-articles-and-seo.md), ADR 0033): what a customer sets, and what
 * the tab reads. The tab is last, after Analytics, and it is empty until a website is connected.
 */

/** One language the site publishes in. The default language has an empty prefix; the others have `/fr`, `/es`. */
export const SiteLanguage = z.object({
  lang: z.string().regex(/^[a-z]{2}$/),
  prefix: z.string().regex(/^(|\/[a-z0-9-]+)$/).default(""),
  default: z.boolean().default(false),
});
export type SiteLanguage = z.infer<typeof SiteLanguage>;

/** The sign-up or offer page per language, and the words its link reads. The customer's promise, in their words. */
export const OfferPath = z.object({
  path: z.string().regex(/^\/[A-Za-z0-9\-/_]*$/).max(200),
  label: z.string().min(1).max(120),
});
export type OfferPath = z.infer<typeof OfferPath>;

/** `website` is the site Joogoos built itself (28): set by the API from what it finds, never chosen on a screen. */
export const SiteKind = z.enum(["static_feed", "wordpress", "website"]);
export type SiteKind = z.infer<typeof SiteKind>;

/**
 * Connecting a website. One per workspace; the first kind needs no write access to anything of the customer's.
 *
 * **The address is not in here.** A business has one website address and it is typed once, on the Business page
 * ([ADR 0040](../../../docs/adr/0040-a-business-has-one-website-address-and-it-is-typed-on-the-business-page.md));
 * this is the publishing half — where articles hang off it, in which languages, and what the offer link says.
 * `cleanDomain` in `site.ts` is the one rule that address passes, and `tidyWebsiteAddress` — which guarded the
 * second field this used to carry — went with the field.
 */
export const ConnectArticleSite = z.object({
  kind: SiteKind.default("static_feed"),
  blogPath: z.string().regex(/^\/[a-z0-9-/]*[a-z0-9]$/).max(100).default("/blog"),
  trailingSlash: z.boolean().default(false),
  languages: z.array(SiteLanguage).min(1).max(12),
  // Keyed by the languages above, so a key is a two-letter code like theirs and nothing longer.
  offers: z.record(z.string().regex(/^[a-z]{2}$/), OfferPath),
});
export type ConnectArticleSite = z.infer<typeof ConnectArticleSite>;

/** The build hook, pasted once and never shown again. Empty clears it. */
export const SetBuildHook = z.object({ url: z.string().max(500) });

/**
 * What the business knows for search — the fourth view on the Business tab, *For search*. Free to edit, always.
 *
 * `neverName` is enforced by code and `neverTopic`/`neverClaim` are given to the writer and checked by the person
 * approving. The ADR says which is which so nobody believes a prompt is a guarantee.
 */
export const SearchSettings = z.object({
  audience: z.string().max(600).default(""),
  neverName: z.array(z.string().min(2).max(120)).max(60).default([]),
  neverTopic: z.array(z.string().min(2).max(200)).max(40).default([]),
  neverClaim: z.array(z.string().min(2).max(200)).max(40).default([]),
});
export type SearchSettings = z.infer<typeof SearchSettings>;

export const SourceKind = z.enum(["note", "url", "file"]);
export type SourceKind = z.infer<typeof SourceKind>;

/** A source added by hand: a pasted note, or a page to read once. */
export const NewSource = z.object({
  kind: SourceKind.default("note"),
  title: z.string().min(1).max(200),
  text: z.string().max(40_000).default(""),
  url: z.string().max(500).default(""),
});
export type NewSource = z.infer<typeof NewSource>;

export const EditionStatus = z.enum(["drafting", "draft", "approved", "published", "updated", "withdrawn", "failed"]);
export type EditionStatus = z.infer<typeof EditionStatus>;

export const ArticleStatus = z.enum(["proposed", "accepted", "dropped"]);
export type ArticleStatus = z.infer<typeof ArticleStatus>;

/** A topic the person is editing, or adding themselves. Every field, in place, for nothing. */
export const EditTopic = TopicProposal.partial().extend({ sourceRefs: z.array(SourceRef).max(20).optional() });
export type EditTopic = z.infer<typeof EditTopic>;

/** How a flag was got past. `stands` makes the person the source, and that is recorded in `audit_log`. */
export const ResolveFlag = z.object({ flagId: z.string().min(1).max(40), how: z.enum(["removed", "cited", "stands"]) });
export type ResolveFlag = z.infer<typeof ResolveFlag>;

/** An edit to the text, by a person. Free, and re-checked: the flags on screen are always about the text on screen. */
export const EditArticle = z.object({
  title: z.string().min(1).max(200).optional(),
  metaDescription: z.string().min(1).max(400).optional(),
  slug: z.string().min(1).max(200).optional(),
  bodyMd: z.string().max(60_000).optional(),
});
export type EditArticle = z.infer<typeof EditArticle>;

// ---------------------------------------------------------------------------------------------------------------
// What the tab reads.
// ---------------------------------------------------------------------------------------------------------------

export interface ArticleSourceView {
  id: string; ref: string; kind: SourceKind; title: string; url: string;
  /** The first line, for the list. The whole text is read when a claim is opened against it. */
  preview: string;
  characters: number;
  fetchedAt: string | null;
}

/** One phrase this site is found for, or one page of it, over the window (21 §13). */
export interface SearchTermView {
  term: string;
  clicks: number;
  impressions: number;
  /** Google's average position, to one decimal. */
  position: number;
}

/**
 * What Google says about this site, as of the last pull (21 §13).
 *
 * Every number here is Google's, over a window ending three days ago — Search Console is always behind, and a
 * screen that pretends otherwise shows a dip that is only missing data.
 */
export interface SearchConsoleView {
  connected: boolean;
  /** Which Google account the grant came from, so somebody can tell whose it is. */
  account: string | null;
  /** The property as Google names it: `sc-domain:overbill.co.uk`, or an address. Null until one is chosen. */
  property: string | null;
  /** What Google says this person verified, offered for them to pick from. Only read when it has been asked for. */
  choices: { property: string; permission: string }[];
  pulledAt: string | null;
  windowDays: number;
  /** The last day the window covers: today minus Google's lag, said out loud. */
  upTo: string | null;
  error: string | null;
  clicks: number;
  impressions: number;
  /** The phrases this site is shown for, busiest first. */
  queries: SearchTermView[];
  /**
   * Page two and three with real impressions: phrases Google already shows this site for and nobody clicks.
   * The best topic list there is, because it is not a guess.
   */
  opportunities: SearchTermView[];
  /** How each published article is doing, matched by its address. */
  articles: { editionId: string | null; path: string; title: string; clicks: number; impressions: number; position: number }[];
}

export interface ArticleSiteView {
  id: string;
  kind: SiteKind;
  baseUrl: string;
  blogPath: string;
  trailingSlash: boolean;
  languages: SiteLanguage[];
  offers: Record<string, OfferPath>;
  /** The feed's own address, for the site's developers. The key is shown once and is not here. */
  feedUrl: string;
  /** The last four characters of the key, so somebody can tell which one a site is holding. */
  feedKeyHint: string;
  buildHookSet: boolean;
  lastBuildAt: string | null;
  lastBuildStatus: number | null;
}

export interface EditionView {
  id: string;
  lang: string;
  slug: string;
  status: EditionStatus;
  path: string;
  url: string;
  title: string;
  metaDescription: string;
  /** The text as the review screen reads it, citations and all. */
  reviewMd: string;
  faq: DraftQuestion[];
  claims: ArticleClaim[];
  links: ArticleLink[];
  flags: ArticleFlag[];
  openFlags: number;
  /** A sample written by `seed-blog`, not by the AI, and labelled as such on screen. */
  sample: boolean;
  version: number;
  approvedAt: string | null;
  publishedAt: string | null;
  /** When the address was last fetched and answered 200. Null means sent but not seen on the site yet. */
  seenLiveAt: string | null;
  /** A translation whose English has moved on since. */
  behind: boolean;
  error: string | null;
}

/**
 * What Google already answers for a topic's phrase, kept on the topic (21 §3).
 *
 * `results` is for the person and **never for the writer**: the ten pages already ranking are the competitors, and
 * ADR 0033 §1's rule — a lookup is exactly where competitor names come from — applies to a page of search results
 * more than to anything else. What the writer is given is `questions` and `related`, which are Google's words about
 * what searchers want rather than a rival's words about themselves.
 */
export interface TopicSerp {
  phrase: string;
  country: string;
  language: string;
  readAt: string;
  results: { position: number; title: string; link: string; domain: string; snippet: string }[];
  questions: string[];
  related: string[];
}

/**
 * Where the business's own site comes in a search already read (21 §3): the first result on its address, or null
 * when it is not in the ten.
 *
 * It costs nothing, because the ten results were already paid for, and it needs nothing from the owner — no Google
 * account, no verified property — which is what it is for: the one ranking a site without Search Console gets. A
 * subdomain counts (`shop.bramleybakehouse.co.uk`); a name that only ends the same way (`notbramleybakehouse.co.uk`)
 * does not.
 */
export function yourPlace(serp: Pick<TopicSerp, "results">, address: string | null | undefined): number | null {
  const host = (address ?? "").trim().toLowerCase().replace(/^[a-z]+:\/\//, "").replace(/[/?#:].*$/, "").replace(/^www\./, "");
  if (!host) return null;
  const hit = serp.results.find((r) => {
    const d = r.domain.toLowerCase().replace(/^www\./, "");
    return d === host || d.endsWith(`.${host}`);
  });
  return hit?.position ?? null;
}

export interface ArticleView {
  id: string;
  status: ArticleStatus;
  targetPhrase: string;
  audience: string;
  intent: ArticleIntent;
  angle: string;
  sourceRefs: string[];
  origin: "planned" | "owner";
  createdAt: string;
  editions: EditionView[];
  /** Null until somebody presses *Check the search results* on it. */
  serp: TopicSerp | null;
}

/** `GET /v1/seo` — everything the tab draws before an article is opened. */
/**
 * What the business already said that the SEO settings would otherwise leave blank (R-1).
 *
 * Connecting a website asked for nothing, so the writer's settings opened empty and looked finished: *who we write
 * for* blank though the Business page says who buys, the offer link `/` though the homepage links to a booking page,
 * and *never name* empty with nothing saying whether the lookup had found anybody to add. These are offered, never
 * saved on the person's behalf: the screen fills its fields with them and Save keeps them.
 */
export interface SeoSuggestions {
  /** *Who buys it* from the Business page, for *who we write for* while that is empty. Empty when the page has none. */
  audience: string;
  /** The booking, quote or contact page the homepage links to, for every article's last link. Null when none looks like one. */
  offer: { path: string; label: string } | null;
  /** The businesses the lookup found, which code keeps out of every article without being asked. Empty is said, not hidden. */
  rivals: string[];
}

/**
 * The pages an article's last link is meant to reach, best first: booking or ordering, then a quote or a demo, then
 * contact. Each word has to start a part of the address, so `/border-collies` is not an order page.
 */
const OFFER_PAGES = [
  /(^|[/_-])(book|appointment|reserve|schedule|order)/i,
  /(^|[/_-])(quote|estimate|demo|trial|get-started|sign-?up)/i,
  /(^|[/_-])(contact|get-in-touch|enquir|inquir)/i,
];

/**
 * The page on a business's own site that an article should end by sending people to, read from its homepage's links.
 *
 * Same host only, never the homepage itself, and the path decides rather than the words, which belong to whoever
 * owns the site; the words are kept as the link's label, or made from the address when the link is a picture.
 */
export function offerFromHomepage(html: string, base: string): { path: string; label: string } | null {
  let origin: URL;
  try { origin = new URL(base); } catch { return null; }
  let best: { rank: number; path: string; label: string } | null = null;
  for (const m of html.matchAll(/<a\b[^>]*?\bhref\s*=\s*["']([^"']+)["'][^>]*>([\s\S]{0,2000}?)<\/a>/gi)) {
    let url: URL;
    try { url = new URL((m[1] ?? "").trim(), origin); } catch { continue; }
    if (url.host !== origin.host || !/^https?:$/.test(url.protocol) || url.pathname === "/") continue;
    // A page, not a post about one: `/blog/how-to-book-a-van` is an article that happens to say *book*.
    if (url.pathname.split("/").filter(Boolean).length > 2) continue;
    const rank = OFFER_PAGES.findIndex((r) => r.test(url.pathname));
    if (rank < 0 || (best && best.rank <= rank)) continue;
    const words = (m[2] ?? "").replace(/<[^>]*>/g, " ").replace(/&amp;/g, "&").replace(/\s+/g, " ").trim().slice(0, 60);
    const fromPath = url.pathname.split("/").filter(Boolean).pop()?.replace(/[-_]+/g, " ") ?? "";
    best = { rank, path: url.pathname, label: words || fromPath.charAt(0).toUpperCase() + fromPath.slice(1) };
  }
  return best ? { path: best.path, label: best.label } : null;
}

export interface SeoResponse {
  /** Null until a website is connected: the tab is one button and a sentence until then. */
  site: ArticleSiteView | null;
  /**
   * The one address this business has, from the Business page (ADR 0040). Null when it has not given one, and
   * then there is nowhere to publish: the tab says so and points at Business rather than asking for it again.
   */
  address: string | null;
  search: SearchSettings;
  sources: ArticleSourceView[];
  articles: ArticleView[];
  /** What each button costs, in dollars, from the operation's own estimate (13). */
  prices: { planTopics: string; writeArticle: string; translateArticle: string; readSerp: string };
  /** What Google says about the site. Null when this box has no Google client, as every unavailable connection answers. */
  google: SearchConsoleView | null;
  /** Set while a paid call for this workspace is in flight, so two people cannot write the same article twice. */
  working: string[];
  /** What the Business page and the lookup already know, for the fields that would otherwise open empty (R-1). */
  suggested: SeoSuggestions;
}

/** `GET /v1/seo/articles/:id` — one article, with every language and the full text of the sources it cites. */
export interface ArticleDetailResponse {
  article: ArticleView;
  sources: (ArticleSourceView & { text: string })[];
}
